import React, { useRef, useState } from "react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { compressImage, formatFileSize } from "../lib/imageCompress";
import { uploadWithRetry } from "../lib/uploadRetry";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function FileUpload({
  folder = "misc",
  value = [],
  onChange,
  multiple = true,
  testId = "file-upload",
}) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  // Progress: { current, total, pct (0..100), uploadingFileSize, uploadingFileName }
  const [progress, setProgress] = useState(null);

  async function handle(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    setProgress({ current: 0, total: files.length, pct: 0 });
    try {
      const paths = [];
      let totalSaved = 0;
      let totalOriginal = 0;
      for (let i = 0; i < files.length; i++) {
        const original = files[i];
        setProgress({
          current: i + 1,
          total: files.length,
          pct: 0,
          uploadingFileName: original.name || `Foto ${i + 1}`,
        });
        // Comprime no cliente — fotos de celular podem ter 8-15MB
        const f = await compressImage(original).catch(() => original);
        const info = f._compressionInfo;
        if (info && !info.skipped) {
          totalOriginal += info.originalSize;
          totalSaved += info.originalSize - info.finalSize;
        }
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", folder);
        const { data } = await uploadWithRetry(
          () =>
            api.post("/upload", fd, {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (evt) => {
                const pct = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
                setProgress((p) => (p ? { ...p, pct } : p));
              },
            }),
          {
            onRetry: (attempt) => {
              toast.info(`Conexão instável — tentando novamente (${attempt}/2)…`, {
                duration: 2500,
              });
              setProgress((p) => (p ? { ...p, pct: 0 } : p));
            },
          },
        );
        paths.push(data.path);
      }
      // Feedback claro só se a compressão valeu (>30% de redução)
      if (totalSaved > 0 && totalOriginal > 0) {
        const savedPct = Math.round((totalSaved / totalOriginal) * 100);
        if (savedPct >= 30) {
          toast.success(
            `${files.length} foto(s) enviada(s) · economizou ${formatFileSize(totalSaved)} (${savedPct}% menor)`,
            { duration: 4000 },
          );
        }
      }
      onChange?.(multiple ? [...value, ...paths] : paths.slice(0, 1));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setBusy(false);
      setProgress(null);
      if (ref.current) ref.current.value = "";
    }
  }

  function remove(idx) {
    const next = value.filter((_, i) => i !== idx);
    onChange?.(next);
  }

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((p, i) => (
          <div
            key={p}
            className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
          >
            <img
              src={fileUrl(p)}
              alt=""
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              data-testid={`${testId}-remove-${i}`}
              onClick={() => remove(i)}
              className="absolute top-0 right-0 bg-black/60 text-white rounded-bl p-0.5"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <label
          className={`w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-xs text-slate-500 cursor-pointer hover:border-blue-500 hover:text-blue-600 ${
            busy ? "opacity-50 pointer-events-none" : ""
          }`}
          data-testid={`${testId}-input-label`}
        >
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          <span className="mt-1">{busy ? "..." : "Enviar"}</span>
          <input
            ref={ref}
            type="file"
            multiple={multiple}
            accept="image/*"
            onChange={handle}
            className="hidden"
            data-testid={`${testId}-input`}
          />
        </label>
      </div>

      {/* Progress bar — só aparece durante upload */}
      {progress && (
        <div className="mt-2" data-testid={`${testId}-progress`}>
          <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1">
            <span>
              Enviando {progress.current}/{progress.total}
              {progress.uploadingFileName ? ` · ${progress.uploadingFileName}` : ""}
            </span>
            <span className="font-bold text-blue-700">{progress.pct}%</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-150"
              style={{
                width: `${
                  ((progress.current - 1) / progress.total) * 100 +
                  progress.pct / progress.total
                }%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
